import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";

import type { FramePrediction } from "../contracts";
import { useTrackingInspectorStore } from "./controller";

type TrackingSource = Exclude<FramePrediction["method"], "unavailable">;

export const TRACKING_SOURCE_APPEARANCES: Record<TrackingSource, { color: string; label: string }> = {
  anchor: { color: "#2b8ae9", label: strings.trackingInspector.sources.anchor },
  sparse: { color: "#d96c07", label: strings.trackingInspector.sources.sparse },
  nano: { color: "#12a578", label: strings.trackingInspector.sources.nano },
  hybrid: { color: "#c98500", label: strings.trackingInspector.sources.hybrid },
  "model-recovery": { color: "#c9578d", label: strings.trackingInspector.sources["model-recovery"] },
};

export function useTrackingSourceAppearance(method: FramePrediction["method"] | undefined) {
  const enabled = useTrackingInspectorStore((state) => state.enabled);
  const showSources = useTrackingInspectorStore((state) => state.showSources);
  if (!enabled || !showSources || !method || method === "unavailable") return undefined;
  return { method, ...TRACKING_SOURCE_APPEARANCES[method] };
}

export function TrackingSourceTag({
  method,
  below = false,
  alignRight = false,
  className,
  assisted = false,
}: {
  method: FramePrediction["method"] | undefined;
  below?: boolean;
  alignRight?: boolean;
  className?: string;
  assisted?: boolean;
}) {
  const appearance = useTrackingSourceAppearance(method);
  if (!appearance) return null;
  return (
    <span
      className={cn(
        "pointer-events-none absolute flex items-center gap-1 whitespace-nowrap rounded-sm border bg-[#1d1c1b] px-1.5 py-0.5 text-sm font-medium leading-5 text-white",
        alignRight ? "right-0" : "left-0",
        below ? "top-0 mt-1" : "bottom-full mb-1",
        className,
      )}
      data-tracking-tag={appearance.method}
      style={{ borderColor: appearance.color }}
    >
      <span aria-hidden="true" className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: appearance.color }} />
      {appearance.label}
      {assisted &&
        method !== "model-recovery" &&
        formatString(strings.trackingInspector.recovery.assistedSuffix, strings.trackingInspector.recovery.assisted)}
    </span>
  );
}
